/**
 * Logique pure des modales de l'onglet Pilotage : clé d'URL d'une modale (`modale`) et navigation
 * « ‹ Précédente · 2 / 7 · Suivante › » entre modales sœurs, sans React. Testée par
 * `pilotage-modal-navigation.test.mjs`.
 */

export type PilotageStep = -1 | 1

/** Clé d'URL d'une modale : `volatilite`, ou `categorie:SUSHIS` pour une modale d'un élément. */
export const pilotageModalKey = (kind: string, id?: string | number): string =>
  id === undefined ? kind : `${kind}:${id}`

/** Lecture inverse de `pilotageModalKey` : l'identifiant garde tout ce qui suit le premier `:`. */
export const parsePilotageModalKey = (modale: string): { kind: string; id: string | null } => {
  const separator = modale.indexOf(':')
  return separator === -1
    ? { kind: modale, id: null }
    : { kind: modale.slice(0, separator), id: modale.slice(separator + 1) }
}

/** Rang voisin, en boucle comme la maquette : après la dernière vient la première. */
export const stepIndex = (index: number, total: number, step: PilotageStep): number =>
  (((index + step) % total) + total) % total

/** La modale sœur de `current` dans `keys`, ou `null` si `current` n'en fait pas partie. */
export const siblingModalKey = (
  keys: readonly string[],
  current: string | undefined,
  step: PilotageStep,
): string | null => {
  const index = current === undefined ? -1 : keys.indexOf(current)
  return index < 0 ? null : keys[stepIndex(index, keys.length, step)]
}

interface NavigationKeyEvent {
  key: string
  altKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  target?: { tagName?: string; isContentEditable?: boolean } | null
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/**
 * Sens de navigation d'une frappe : ← précédente, → suivante. Rien avec un modificateur, ni depuis
 * un champ de saisie, où les flèches déplacent le curseur.
 */
export const navigationStepOfKey = (event: NavigationKeyEvent): PilotageStep | null => {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return null
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return null
  const target = event.target
  if (target?.isContentEditable || EDITABLE_TAGS.has(target?.tagName?.toUpperCase() ?? '')) {
    return null
  }
  return event.key === 'ArrowLeft' ? -1 : 1
}
